import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {of, Subject} from 'rxjs';
import {debounceTime, mergeAll, tap} from 'rxjs/operators';
import {AsaxHeaderService, HeaderFilter} from 'asax-common';
import {ElasticParserFactory, ElasticParserType, ElasticValidator, ElasticValidatorFactory} from 'asax-elastic';
import {TextSearchInput} from "../search-input.model";

export const searchInputDebounceTime = 500;

@Component({
  selector: 'asax-header-search-input-text',
  templateUrl: './search-input-text.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchInputTextComponent implements OnInit, OnChanges {
  @Input() searchInput: TextSearchInput;

  @Output() outFilter = new EventEmitter<HeaderFilter>();

  isInvalid: boolean;
  errorMessage: string;

  valueSubject = new Subject<string>();
  valueSubjectWithDebounceTime = new Subject<string>();

  elasticValidator: ElasticValidator;

  constructor(private readonly elasticValidatorFactory: ElasticValidatorFactory,
              private readonly asaxHeaderService: AsaxHeaderService,
              private readonly elasticParserFactory: ElasticParserFactory,
              private readonly changeDetectorRef: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    this.elasticValidator = this.elasticValidatorFactory.getElasticValidator(this.searchInput.elasticValidatorType);

    of(
      this.valueSubjectWithDebounceTime.asObservable().pipe(debounceTime(searchInputDebounceTime)),
      this.valueSubject.asObservable()
    ).pipe(
      mergeAll(),
      tap(() => this.isInvalid = undefined),
      tap(() => this.validateValue()),
      tap(() => this.changeDetectorRef.markForCheck()),
      tap(() => this.emitFilter())
    ).subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.valueChanged();
  }

  valueChangedWithDebounceTime(): void {
    this.valueSubjectWithDebounceTime.next(undefined);
  }

  valueChanged(): void {
    this.valueSubject.next(undefined);
  }

  validateValue(): void {
    if (this.searchInput?.value?.length > 0 && this.elasticValidator) {
      const {isInvalid, errorMessage} = this.elasticValidator.validate(this.searchInput.value);
      this.isInvalid = isInvalid;
      this.errorMessage = errorMessage;
    }
  }

  clearSearchInputValue(): void {
    this.searchInput.value = '';
    this.valueChanged();
  }

  onExampleClick(example: string): void {
    this.searchInput.value = example;
    this.valueChanged();
  }

  emitFilter(): void {
    const filter = {
      id: this.searchInput.id,
      value: this.searchInput.value,
      valueParsed: this.searchInput.value,
      valueToDisplay: this.searchInput.value,
      label: this.searchInput.label,
      isDisabled: this.searchInput.isDisabled,
      isInvalid: this.isInvalid,
      elasticParserType: this.searchInput.elasticParserType
    };

    const idsToDrawTheCircle = new Set(['raDec', 'galactic', 'ecliptic']);

    if (idsToDrawTheCircle.has(this.searchInput.id) && this.searchInput.value) {
      const [raDecString, radiusString] = this.searchInput.value.split(',');
      const radius = radiusString ? +radiusString : 10;

      const parserTypeMap: Record<string, string> = {
        raDec: 'ra-dec',
        galactic: 'galactic',
        ecliptic: 'ecliptic'
      };

      const parserType = parserTypeMap[this.searchInput.id] as ElasticParserType;
      const decParserParser = this.elasticParserFactory.getElasticParser(parserType);

      const parsedValue = decParserParser.parseValue(raDecString, {property: {ra: 'ra', dec: 'dec', footprint: 'footprint'}});
      const ra = parsedValue?.astrofilt?.footprint?.shape?.coordinates[0];
      const dec = parsedValue?.astrofilt?.footprint?.shape?.coordinates[1];
      if (ra && dec && radius) {
        this.asaxHeaderService.setRaDecSearchRadius({ra: ra, dec: dec, radius: radius});
      }
    }
    this.outFilter.emit(filter);
  }
}
